import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CModal, CModalBody, CModalTitle, CButton, CProgress } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import XLSX from 'xlsx';
import PropTypes from 'prop-types';

import { OPERATORS_REQUIRE_VALUE } from '../../../../../../constants';
import CLabel from '../../../../../migration/CLabel';
import CFormGroup from '../../../../../migration/CFormGroup';
import { CInputFile, CSelect } from '../../../../../migration/CInput';

const ImportTableModal = ({
     show,
     onClose,
     toggleModal,
     exportToFile,
     getHeaderValues,
     tableRow,
     setTableRow,
     resetBtnSaveStatus,
     variablesUseRegex,
     IsExistedWithName,
}) => {
     const [importedFileName, setImportedFileName] = useState('');
     const [importedValues, setImportedValues] = useState([]);
     const [importMethod, setImportMethod] = useState('');
     const [isUploadedError, setIsUploadedError] = useState(false);
     const [isUploadSuccess, setIsUploadSuccess] = useState(false);
     const [isSelectFile, setIsSelectFile] = useState(false);
     const inputFileRef = useRef(null);

     const IMPORT_OPTIONS = [
          {
               LABEL: 'Replace entire existing table with new values',
               VALUE: 'replace',
               OPTIONAL_TEXT: 'The new records will replace your entire existing table.',
          },
          {
               LABEL: 'Insert new values at bottom of existing table',
               VALUE: 'insert bottom',
               OPTIONAL_TEXT: 'The new records will be added to the bottom of your existing table.',
          },
          {
               LABEL: 'Insert new values at the top of the exist table',
               VALUE: 'insert top',
               OPTIONAL_TEXT: 'The new records will be added to the top of your existing table. ',
          },
     ];

     const resetState = () => {
          setImportedFileName('');
          setImportedValues([]);
          setImportMethod('');
          setIsUploadedError(false);
          setIsUploadSuccess(false);
     };

     const handleSelectImportMethod = useCallback((e) => {
          setImportMethod(e.target.value);
     }, []);

     const clickCancelBtn = () => {
          if (isUploadedError) {
               inputFileRef.current.value = ''; // Set input file value = '', so the user can upload the same file again
               resetState();
          }

          toggleModal();
     };

     const uploadFile = (e) => {
          const { files } = e.target;

          if (files?.length > 0) {
               let reader = new FileReader();

               reader.onload = function (_) {
                    // Use reader.result
                    let workbook = XLSX.read(reader.result, {
                         type: 'binary',
                         raw: true, // Plain text parsing will not parse values ** (for example: Aug 2019 will not be parsed to 8/1/2019)
                    });

                    setImportedFileName(files[0].name);

                    let jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { range: 1, raw: false, defval: '' });

                    if (jsonData.length > 0) {
                         setImportedValues(jsonData);
                    }
               };

               reader.readAsBinaryString(files[0]);
          }

          handleCancel();
     };

     const uploadTable = () => {
          setIsUploadedError(false);

          if (!importedValues[0]) {
               return setIsUploadedError(true);
          }

          let { secondRowValues } = getHeaderValues();
          let importedHeaderValues = Object.entries(importedValues[0]).map(([key, _]) => key.trim());

          // Make sure all keys of first element match with second row values, the order isn't matter
          let hasError = secondRowValues.some((secondRowValue) => {
               return importedHeaderValues.indexOf(secondRowValue) === -1; // Not found, break
          });

          if (hasError) {
               setIsUploadedError(true);
          } else {
               // Convert to an array of arrays of values from importedValues in order of secondRowValues
               let aoaImportedValues = importedValues.map((importedValue) => secondRowValues.map((secondRowValue) => importedValue[secondRowValue]));

               let newImportedValues = [];
               let isConditionValueValid = true;

               for (const rowValues of aoaImportedValues) {
                    let valuePosition = 0;
                    let newRowConditions = [];

                    // eslint-disable-next-line
                    tableRow[0].conditions.some((condition) => {
                         // Set like this, if wants to set directly to g0 object, we will have to clone deep to assign new value correctly
                         let newValue = '';
                         let isRegex = false;
                         const conditionG0 = condition.g0;

                         if (variablesUseRegex.includes(conditionG0.type)) {
                              // crawl-block has a 'Match Type' key with no regex column; rule-edit (IsExistedWithName) does not
                              const matchTypeKey = !IsExistedWithName && conditionG0.key === 'Match Type';
                              const valuePositionNext = matchTypeKey ? valuePosition : valuePosition + 1;
                              newValue = rowValues[valuePosition];

                              // rule-edit: only require value when operator is in OPERATORS_REQUIRE_VALUE; crawl-block: always require
                              const requiresValue = IsExistedWithName ? OPERATORS_REQUIRE_VALUE.includes(conditionG0.operator) : true;

                              if (requiresValue && !newValue) {
                                   isConditionValueValid = false;
                                   return true; // Invalid value, break;
                              }

                              if (rowValues[valuePositionNext] === '1') {
                                   isRegex = true;
                              } else if (rowValues[valuePositionNext] === '0') {
                                   isRegex = false;
                              } else if (!matchTypeKey) {
                                   isConditionValueValid = false;
                                   return true; // Invalid value, break;
                              }

                              valuePosition += matchTypeKey ? 1 : 2;
                         }

                         newRowConditions.push({ ...condition, g0: { ...conditionG0, value: newValue, isRegex } });
                         return false;
                    });

                    let newCustomVariables = IsExistedWithName
                         ? tableRow[0].customVariables.map((customVariable) => {
                                let newValue = rowValues[valuePosition];
                                valuePosition++;
                                const found = IsExistedWithName(newValue);
                                return { ...customVariable, id: found ? found.id : '' };
                           })
                         : [];

                    newImportedValues.push({ conditions: newRowConditions, customVariables: newCustomVariables });
               }

               if (isConditionValueValid) {
                    setImportedValues(newImportedValues);
                    setIsUploadSuccess(true);
               } else {
                    setIsUploadedError(true);
               }
          }
     };

     const applyChanges = () => {
          let isTableEmpty = tableRow.length === 0;

          if (isTableEmpty) {
               // Table is empty, replace table row with imported rows
               setTableRow(importedValues);
          } else {
               switch (importMethod) {
                    case 'replace':
                         setTableRow(importedValues);
                         break;
                    case 'insert bottom':
                         setTableRow((preTable) => [...preTable, ...importedValues]);
                         break;
                    case 'insert top':
                         setTableRow([...importedValues, ...tableRow]);
                         break;
                    default:
               }
          }

          inputFileRef.current.value = ''; // Set input file value = '', so the user can upload the same file again
          resetState();
          resetBtnSaveStatus(); // Enable Save Changes button
          toggleModal();
     };

     const cancelChange = () => {
          inputFileRef.current.value = ''; // Set input file value = '', so the user can upload the same file again
          resetState();
     };

     const handleCancel = () => {
          if (isSelectFile) {
               setTimeout(() => {
                    setIsSelectFile(false);
               }, 200);
          }
     };

     useEffect(() => {
          document.addEventListener('cancel', handleCancel);

          return () => {
               document.removeEventListener('cancel', handleCancel);
          };
     }, [isSelectFile]); // eslint-disable-line

     return (
          <div className='import-modal'>
               <CModal visible={show} onClose={onClose} alignment='center' portal={false} className={isSelectFile ? 'pe-none' : ''}>
                    <CModalBody>
                         <div className='import-modal-body'>
                              <CIcon icon='cil-x' onClick={onClose} className='icon-close-popup' />
                              <CModalTitle>Import Table Values</CModalTitle>
                              <p>
                                   Use this feature to import new values for your table. You can replace all existing values or add new values to the top or bottom of
                                   your table. To begin, export your Table Template below. Then populate the values and import it.
                              </p>
                              <div className={`import-modal-step${isUploadSuccess ? ' import-modal-step-disable' : ''}`}>
                                   <p>Step 1: Download my table's template:</p>
                                   <CButton color='light' onClick={() => exportToFile('csv', 'table-template', true)} disabled={isUploadSuccess}>
                                        DOWNLOAD
                                   </CButton>
                              </div>
                              <p>Upload my new values:</p>
                              <CFormGroup className={`col-12${isUploadSuccess ? ' import-modal-file-disable' : ''}`}>
                                   <CInputFile
                                        innerRef={inputFileRef}
                                        custom
                                        id='custom-file-input'
                                        onChange={uploadFile}
                                        onClick={() => setIsSelectFile(true)}
                                        disabled={isUploadSuccess}
                                   />
                                   <CLabel htmlFor='custom-file-input' variant='custom-file'>
                                        {importedFileName || 'Choose file...'}
                                   </CLabel>
                              </CFormGroup>
                              <CFormGroup className={`${isUploadSuccess ? ' import-modal-select-disable' : ''}`}>
                                   <CSelect value={importMethod} onChange={handleSelectImportMethod} disabled={isUploadSuccess}>
                                        <option value='' disabled hidden>
                                             Select an option...
                                        </option>
                                        {IMPORT_OPTIONS.map((importOption) => (
                                             <option key={importOption.VALUE} value={importOption.VALUE}>
                                                  {importOption.LABEL}
                                             </option>
                                        ))}
                                   </CSelect>
                              </CFormGroup>
                              {!isUploadSuccess && (
                                   <div className='import-modal-button'>
                                        <CButton color='primary' disabled={!importedFileName || !importMethod} onClick={uploadTable}>
                                             UPLOAD
                                        </CButton>
                                        <CButton color='light' type='button' onClick={clickCancelBtn}>
                                             CANCEL
                                        </CButton>
                                   </div>
                              )}
                              {isUploadedError && (
                                   <div className='import-modal-error'>
                                        <p>
                                             File upload error. Please make sure you are using your table template and saving the file as the same format it was
                                             downloaded in.
                                        </p>
                                   </div>
                              )}
                              {isUploadSuccess && (
                                   <div className='import-modal-progress'>
                                        <CProgress color='success' value={100} />
                                        <p>
                                             File processed successfully. We found {importedValues.length} rows of data in your upload.{' '}
                                             {IMPORT_OPTIONS.find((importOption) => importOption.VALUE === importMethod).OPTIONAL_TEXT}
                                        </p>
                                        <div className='import-modal-button'>
                                             <CButton color='primary' disabled={!importedValues || !importMethod} onClick={applyChanges}>
                                                  APPLY CHANGES
                                             </CButton>
                                             <CButton color='light' type='button' onClick={cancelChange}>
                                                  CANCEL CHANGES
                                             </CButton>
                                        </div>
                                   </div>
                              )}
                         </div>
                    </CModalBody>
               </CModal>
          </div>
     );
};

ImportTableModal.propTypes = {
     show: PropTypes.any,
     onClose: PropTypes.func,
     toggleModal: PropTypes.func,
     exportToFile: PropTypes.any,
     getHeaderValues: PropTypes.func,
     tableRow: PropTypes.array,
     setTableRow: PropTypes.func,
     resetBtnSaveStatus: PropTypes.func,
     variablesUseRegex: PropTypes.array,
     IsExistedWithName: PropTypes.func,
};

export default ImportTableModal;
